import React, { useEffect, useMemo, useState } from "react";
import { adminService } from "../admin.service";
import { AdminOffer } from "../admin-types";
import { Column, Empty, Panel, SortConfig, SortDirection, Table } from "../AdminUI";

type OfferListRow = AdminOffer & {
  company_offer_count?: number;
};

interface Props {
  onSelectOffer: (offer: AdminOffer) => void;
  cached: AdminOffer[] | null;
  onCache: (offers: AdminOffer[]) => void;
  refreshKey: number;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export default function OffersList({
  onSelectOffer,
  cached,
  onCache,
  refreshKey,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offers, setOffers] = useState<OfferListRow[]>((cached || []) as OfferListRow[]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const handleSort = (key: string) => {
    let direction: SortDirection = "asc";
    if (sortConfig && sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setPage(1);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const orderBy = useMemo(() => {
    if (!sortConfig?.key || !sortConfig?.direction) return "";
    const sortKeyMap: Record<string, string> = {
      position: "name",
      company_name: "company",
      number_of_vacancies: "vacancies",
      end_date: "exp_date",
      company_offer_count: "company_offer_count",
    };
    const key = sortKeyMap[sortConfig.key] || sortConfig.key;
    return sortConfig.direction === "desc" ? `-${key}` : key;
  }, [sortConfig]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await adminService.fetchAllOffersForAdmin({
          page,
          size: pageSize,
          search: debouncedSearch,
          orderBy,
        });
        if (!mounted) return;
        setOffers((result.rows || []) as OfferListRow[]);
        setTotal(result.total || 0);
        setHasNext(result.hasNext);
        setHasPrevious(result.hasPrevious);
        onCache(result.rows || []);
      } catch (e: any) {
        if (mounted) setError(e.message || "No se pudieron cargar las ofertas.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [refreshKey, page, pageSize, debouncedSearch, orderBy]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const cols: Column<OfferListRow>[] = [
    {
      key: "position",
      header: "Oferta",
      sortable: true,
      render: (r) => (
        <div>
          <div className="font-medium text-slate-800">{r.position || "—"}</div>
        </div>
      ),
    },
    {
      key: "company_name",
      header: "Empresa",
      sortable: true,
      render: (r) => (
        <div>
          <div className="font-medium text-slate-800">{r.company?.name || "—"}</div>
          <div className="text-slate-500 text-xs">{r.company?.email || "—"}</div>
        </div>
      ),
    },
    {
      key: "location",
      header: "Ubicación Oferta",
      sortable: true,
      render: (r) => {
        const location = [r.city, r.country]
          .filter(Boolean)
          .join(", ");
        return location || r.location || "—";
      },
    },
    {
      key: "created_at",
      header: "Inicio",
      sortable: true,
      render: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"),
    },
    {
      key: "end_date",
      header: "Fin",
      sortable: true,
      render: (r) => (r.end_date ? new Date(r.end_date).toLocaleDateString() : "—"),
    },
    {
      key: "number_of_vacancies",
      header: "Vacantes",
      sortable: true,
      render: (r) => r.number_of_vacancies ?? 1,
    },
    {
      key: "company_offer_count",
      header: (
        <span>
          Ofertas
          <br />
          Empresa
        </span>
      ),
      sortable: true,
      render: (r) => r.company_offer_count ?? 0,
    },
    {
      key: "apply_count",
      header: "Aplicaciones",
      sortable: true,
      render: (r) => <span className="font-bold text-slate-700">{r.apply_count || 0}</span>,
    },
    {
      key: "status",
      header: "Estado",
      sortable: true,
      render: (r) => {
        const s = (r.status || "").toLowerCase();
        let badgeClass = "";
        let label = r.status || "—";
        if (s === "publish") {
          badgeClass = "status-publish";
          label = "Publicada";
        } else if (s === "draft") {
          badgeClass = "status-draft";
          label = "Borrador";
        } else if (s === "closed" || s === "expired" || s === "inactive") {
          badgeClass = "status-closed";
          label = "Cerrada";
        }
        return <span className={`badge ${badgeClass}`}>{label}</span>;
      },
    },
  ];

  return (
    <Panel
      title="Ofertas"
      right={
        <div className="admin-list-controls">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar oferta, empresa o candidato (nombre/NIF)..."
            className="input"
          />
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="admin-page-size"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size} / pág.
              </option>
            ))}
          </select>
          <span className="admin-list-meta">{total} resultados</span>
        </div>
      }
    >
      {loading ? (
        <Empty>Cargando ofertas…</Empty>
      ) : error ? (
        <Empty>Error: {error}</Empty>
      ) : offers.length ? (
        <>
          <Table
            columns={cols}
            rows={offers}
            keyField="id"
            onRowClick={onSelectOffer}
            onSort={handleSort}
            sortConfig={sortConfig}
          />
          <div className="admin-pagination">
            <button
              className="action-btn ghost"
              disabled={!hasPrevious || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </button>
            <span className="admin-pagination-info">
              Página {page} de {totalPages}
            </span>
            <button
              className="action-btn ghost"
              disabled={!hasNext || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </button>
          </div>
        </>
      ) : (
        <Empty>No se encontraron ofertas.</Empty>
      )}
    </Panel>
  );
}
